/**
 * The five bookable bed types for the network-wide picker (M12-T02, ADR-0027).
 *
 * Includes **General ward** — bookable per the owner decision — which has no
 * filter chip (the chip row exposes only ICU/Maternity/Private/Executive). The
 * picker is not a chip row (ADR-0027 note): a type maps to its `BedState`
 * free/total columns, and to a Row-1 chip only where one exists. Chip vocabulary
 * (`chips.ts`) is not touched.
 */
import type { BedState } from '../api/hospitals'

/** The numeric free/total inventory columns on `BedState` (excludes the
 * `emergency_status`/`ambulance_available`/`updated_at` fields) so sorting and
 * aggregation stay arithmetic-safe. */
type BedFreeKey = Extract<keyof BedState, `${string}_free`>
type BedTotalKey = Extract<keyof BedState, `${string}_total`>

export interface BedTypeDef {
  /** Matches the M10 booking `bed_type` + `/book/:slug?type=`. */
  key: string
  /** Display name, as the selector lists it. Title case, stands alone. */
  label: string
  /**
   * The COUNTABLE noun, for lines like "3 maternity beds" (M21-T01).
   *
   * Separate from `label` because a label that reads correctly on its own does
   * not necessarily read correctly after a number. "Executive suite" is the
   * clear case: an executive suite is not a *bed*, so the honest count is
   * "3 executive suites" and `${label} beds` would have produced "3 executive
   * suite beds". "General ward" is the other: title case mid-sentence reads as
   * a proper noun.
   *
   * Singular only. All five pluralise by adding "s", which is checked by a test
   * — if a sixth type ever needs an irregular plural this becomes two fields,
   * and that is the moment to notice, not a reason to add them now.
   */
  noun: string
  /** Row-1 chip id to pre-apply on results, or null (General ward has none). */
  chip: string | null
  free: BedFreeKey
  total: BedTotalKey
}

// Order per the wireframe `BedTypePickerMobile`.
export const BED_TYPE_DEFS = [
  {
    key: 'icu',
    label: 'ICU',
    noun: 'ICU bed',
    chip: 'icu',
    free: 'icu_free',
    total: 'icu_total',
  },
  {
    key: 'general',
    label: 'General ward',
    noun: 'general ward bed',
    chip: null,
    free: 'general_free',
    total: 'general_total',
  },
  {
    key: 'maternity',
    label: 'Maternity',
    noun: 'maternity bed',
    chip: 'maternity',
    free: 'maternity_free',
    total: 'maternity_total',
  },
  {
    key: 'private',
    label: 'Private ward',
    noun: 'private ward bed',
    chip: 'private',
    free: 'private_free',
    total: 'private_total',
  },
  {
    key: 'executive',
    label: 'Executive suite',
    noun: 'executive suite',
    chip: 'executive',
    free: 'executive_free',
    total: 'executive_total',
  },
] as const satisfies readonly BedTypeDef[]

/**
 * How many of this bed type are free, in words (M21-T01).
 *
 * "No ICU beds" reads as a fact; "0 ICU beds" reads as a missing number. That
 * distinction came from the home strip and is the reason zero gets its own
 * branch rather than falling through the plural.
 *
 * Lives beside the definitions rather than in the component so that every
 * surface counting beds says it the same way. Two already do — the home strip
 * and, from M21-T02, the bed page.
 */
export function bedFreeLabel(def: BedTypeDef, free: number): string {
  if (free <= 0) return `No ${def.noun}s`
  return free === 1 ? `1 ${def.noun}` : `${free} ${def.noun}s`
}

/** The ICU definition, which the home strip pins deliberately (M21-T01). */
export const ICU_BED_TYPE: BedTypeDef = BED_TYPE_DEFS[0]

/*
 * `bedTypeDestination()` USED TO LIVE HERE and was removed by M21-T04.
 *
 * It built `/?row1=<chip>&bedtype=<key>` — where picking a bed type landed while
 * `/beds` was an interstitial. ADR-0046 made the type a filter answered in
 * place, so nothing constructs that URL any more.
 *
 * Deleted rather than left exported-but-unused. An orphaned helper that still
 * looks live is how the old hop gets reintroduced by someone wiring a new
 * surface and reaching for the obvious function.
 *
 * THE URL ITSELF IS NOT RETIRED, and must not be. `/?row1=…&bedtype=…` is still
 * read: `App.tsx` routes to results on `bedtype`, and `HospitalResults` sorts by
 * that type's free count. ADR-0036 keeps the chip vocabulary as a URL and API
 * contract precisely so links people already hold keep working — this is the
 * same reasoning. We stopped WRITING that URL; we still READ it.
 */
